import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownToLine, Pause, Play, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { openSse } from "@/lib/api";
import { cn } from "@/lib/utils";

interface RuntimeLine {
  n: number;
  stream: "stdout" | "stderr";
  text: string;
}

type StreamState =
  | { kind: "connecting" }
  | { kind: "live" }
  | { kind: "historical" }
  | { kind: "closed" }
  | { kind: "unavailable"; reason: string };

const UNAVAILABLE_COPY: Record<string, string> = {
  static_output: "This deployment is a static build — there's no server process, so no runtime logs.",
  never_ran: "This deployment never started a runtime (build failed, cancelled or skipped).",
  not_started: "Runtime logs will appear once the deployment is live.",
  expired: "This deployment's container is gone — its retained runtime logs have expired.",
  stream_error: "Couldn't read the container's log stream. Try again shortly.",
};

/**
 * 006 US1 — RuntimeLogs: SSE tail of the runtime container's stdout/stderr
 * (Docker json-file backed; see the API route for retention semantics).
 * Search filters client-side and keeps matching live as new lines stream in.
 */
export function RuntimeLogs({ deploymentId }: { deploymentId: string }) {
  const [lines, setLines] = useState<RuntimeLine[]>([]);
  const [state, setState] = useState<StreamState>({ kind: "connecting" });
  const [paused, setPaused] = useState(false);
  const [query, setQuery] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const counter = useRef(0);

  useEffect(() => {
    setLines([]);
    counter.current = 0;
    setState({ kind: "connecting" });
    const es = openSse(`/deployments/${deploymentId}/runtime-logs`);
    es.addEventListener("status", (e) => {
      try {
        const s = JSON.parse((e as MessageEvent).data) as { state: string; reason?: string };
        if (s.state === "live") setState({ kind: "live" });
        else if (s.state === "historical") setState({ kind: "historical" });
        else if (s.state === "unavailable") setState({ kind: "unavailable", reason: s.reason ?? "expired" });
      } catch { /* ignore */ }
    });
    es.addEventListener("line", (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as { stream: "stdout" | "stderr"; text: string };
        counter.current += 1;
        const line = { n: counter.current, ...data };
        setLines((prev) => (prev.length >= 5000 ? prev.slice(1).concat(line) : prev.concat(line)));
      } catch { /* ignore */ }
    });
    es.addEventListener("end", () => {
      setState((s) => (s.kind === "unavailable" ? s : { kind: "closed" }));
      es.close();
    });
    es.addEventListener("error", () => setState((s) => (s.kind === "unavailable" ? s : { kind: "closed" })));
    return () => es.close();
  }, [deploymentId]);

  const visible = useMemo(() => {
    if (!query) return lines;
    const q = query.toLowerCase();
    return lines.filter((l) => l.text.toLowerCase().includes(q));
  }, [lines, query]);

  useEffect(() => {
    if (paused) return;
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [visible, paused]);

  function download() {
    const text = lines.map((l) => l.text).join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `runtime-${deploymentId.slice(0, 8)}.log`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (state.kind === "unavailable") {
    return (
      <div className="of-card p-10 text-center text-sm text-muted-foreground">
        {UNAVAILABLE_COPY[state.reason] ?? UNAVAILABLE_COPY.expired}
      </div>
    );
  }

  return (
    <div className="of-card overflow-hidden">
      <div className="of-card-header flex-wrap gap-3 gap-y-2">
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 text-xs font-medium",
              state.kind === "live" ? "text-success" : state.kind === "connecting" ? "text-attention" : "text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                state.kind === "live" ? "bg-success animate-pulse-dot" : state.kind === "connecting" ? "bg-attention" : "bg-muted-foreground",
              )}
            />
            {state.kind === "live" ? "Live" : state.kind === "historical" ? "Historical" : state.kind === "connecting" ? "Connecting" : "Ended"}
          </span>
          <span className="text-2xs text-subtle-foreground font-mono">
            {query ? `${visible.length} / ${lines.length}` : `${lines.length} lines`}
          </span>
        </div>
        <div className="relative flex-1 max-w-xs min-w-[160px]">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-subtle-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search logs…"
            className="h-7 pl-7 text-xs font-mono"
          />
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" onClick={() => setPaused((p) => !p)} title={paused ? "Resume autoscroll" : "Pause autoscroll"}>
            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={download} title="Download .log">
            <ArrowDownToLine className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setLines([])} title="Clear view">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="of-mono-surface max-h-[60vh] overflow-auto">
        <pre className="font-mono text-xs leading-5 px-0 py-3">
          {visible.length === 0 ? (
            <div className="px-4 text-subtle-foreground italic">
              {query
                ? "No lines match your search."
                : state.kind === "closed"
                  ? "No runtime output captured."
                  : "Waiting for runtime output — hit the deployment URL to generate some…"}
            </div>
          ) : null}
          {visible.map((l) => (
            <div
              key={l.n}
              className={cn(
                "grid grid-cols-[3.5rem_1fr] gap-3 px-4 py-px hover:bg-subtle/40 transition-colors",
                l.stream === "stderr" && "text-danger",
              )}
            >
              <span className="text-right text-subtle-foreground select-none tabular-nums">{l.n}</span>
              <span className="whitespace-pre-wrap break-words">{l.text}</span>
            </div>
          ))}
          <div ref={bottomRef} />
        </pre>
      </div>
    </div>
  );
}
